import { useState, useEffect } from 'react';
import { useLanguage } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ShieldCheck, Cookie } from 'lucide-react';

export default function CookieBanner() {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({ essential: true, analytics: false });

  useEffect(() => {
    try {
      const savedConsent = localStorage.getItem('cote-terrasse-cookies-consent');
      if (!savedConsent) {
        setIsVisible(true);
      } else {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
        // Émettre un événement pour d'éventuels scripts tiers
        triggerAnalytics(parsed.analytics);
      }
    } catch {
      setIsVisible(true);
    }
  }, []);

  const triggerAnalytics = (allowed) => {
    window.cookiesConsent = { analytics: allowed };
    // Si Google Analytics était configuré, on l'activerait ici :
    // if (allowed) { initGoogleAnalytics(); } else { disableGoogleAnalytics(); }
    const event = new CustomEvent('cookiesConsentChanged', { detail: { analytics: allowed } });
    window.dispatchEvent(event);
  };

  const handleAcceptAll = () => {
    const newConsent = { essential: true, analytics: true };
    saveConsent(newConsent);
  };

  const handleDeclineAll = () => {
    const newConsent = { essential: true, analytics: false };
    saveConsent(newConsent);
  };

  const handleSaveSettings = () => {
    saveConsent(consent);
  };

  const saveConsent = (newConsent) => {
    try {
      localStorage.setItem('cote-terrasse-cookies-consent', JSON.stringify(newConsent));
      setConsent(newConsent);
      triggerAnalytics(newConsent.analytics);
      setIsVisible(false);
      setShowSettings(false);
    } catch (e) {
      console.warn('Unable to save cookies consent:', e);
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[420px] bg-white border border-garden-800/10 shadow-2xl rounded-3xl p-6 z-50 animate-fade-up"
      data-testid="cookie-banner"
    >
      {!showSettings ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-garden-100 rounded-full flex items-center justify-center text-garden-800 shrink-0">
              <Cookie size={20} />
            </div>
            <h3 className="font-heading font-semibold text-lg text-garden-800">{t('cookies.title')}</h3>
          </div>
          
          <p className="text-sm text-charcoal/80 leading-relaxed">
            {t('cookies.bannerText')}
          </p>

          <div className="flex flex-col gap-2 pt-2 sm:flex-row">
            <Button 
              onClick={handleAcceptAll} 
              data-testid="cookie-accept-all"
              className="bg-garden-800 hover:bg-garden-900 text-white rounded-full text-xs font-medium px-4 h-9 flex-1"
            >
              {t('cookies.accept')}
            </Button>
            <Button 
              onClick={handleDeclineAll} 
              data-testid="cookie-decline-all"
              variant="outline" 
              className="border-garden-200 text-garden-850 hover:bg-garden-50 rounded-full text-xs font-medium px-4 h-9 flex-1"
            >
              {t('cookies.decline')}
            </Button>
          </div>
          
          <div className="text-center">
            <button 
              onClick={() => setShowSettings(true)}
              data-testid="cookie-open-settings"
              className="text-xs text-garden-700 hover:text-garden-900 underline font-medium"
            >
              {t('cookies.customize')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-garden-100 rounded-full flex items-center justify-center text-garden-800 shrink-0">
              <ShieldCheck size={20} />
            </div>
            <h3 className="font-heading font-semibold text-lg text-garden-800">{t('cookies.title')}</h3>
          </div>

          <div className="space-y-4 max-h-[220px] overflow-y-auto pr-1">
            {/* Essential cookies */}
            <div className="flex items-start justify-between gap-4 p-3 bg-garden-50/50 rounded-2xl border border-garden-850/5">
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-garden-900 block">
                  {t('cookies.essentialTitle')}
                </Label>
                <p className="text-[11px] text-charcoal/60 leading-normal">
                  {t('cookies.essentialDesc')}
                </p>
              </div>
              <Switch checked={true} disabled className="data-[state=checked]:bg-garden-800 mt-1 scale-75 origin-top-right" />
            </div>

            {/* Analytics cookies */}
            <div className="flex items-start justify-between gap-4 p-3 bg-garden-50/50 rounded-2xl border border-garden-850/5">
              <div className="space-y-1">
                <Label htmlFor="cookie-analytics" className="text-xs font-semibold text-garden-900 block cursor-pointer">
                  {t('cookies.analyticsTitle')}
                </Label>
                <p className="text-[11px] text-charcoal/60 leading-normal">
                  {t('cookies.analyticsDesc')}
                </p>
              </div>
              <Switch 
                id="cookie-analytics"
                data-testid="cookie-switch-analytics"
                checked={consent.analytics} 
                onCheckedChange={(checked) => setConsent({ ...consent, analytics: checked })}
                className="data-[state=checked]:bg-garden-800 mt-1 scale-75 origin-top-right"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-1 sm:flex-row">
            <Button 
              onClick={handleSaveSettings} 
              data-testid="cookie-save-settings"
              className="bg-garden-800 hover:bg-garden-900 text-white rounded-full text-xs font-medium h-9 flex-1"
            >
              {t('cookies.save')}
            </Button>
            <Button 
              onClick={() => setShowSettings(false)} 
              variant="outline" 
              className="border-garden-200 text-garden-850 hover:bg-garden-50 rounded-full text-xs font-medium h-9 flex-1"
            >
              {t('cookies.decline')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
